import { useEffect, useState, useCallback, useMemo, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Add, Clear, Delete, Edit, HelpOutline, Refresh, Shield } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import type { UserWithGroups } from '../../types';
import { useListSearchParams } from '../../hooks/useListSearchParams';
import LinkPagination from '../LinkPagination';
import LinkTableCell from '../LinkTableCell';
import FilteredListLayout from '../FilteredListLayout';
import ListActionButton from '../ListActionButton';
import { RequirePermission, usePermissions } from '../../contexts/PermissionContext';
import { useAuth } from '../../contexts/AuthContext';
import UserTelephonyAvailabilityStatus from './UserTelephonyAvailabilityStatus';

const USER_LIST_DEFAULT_ORDERING = 'email';
const USER_LIST_ORDERING_EMAIL = 'email';
const USER_LIST_ORDERING_NAME = 'name';
const USER_LIST_ORDERING_TELEPHONY_EXTENSION = 'telephony_extension';
const USER_LIST_FILTER_KEYS_WITHOUT_TELEPHONY = ['status', 'telephony_availability', 'search'];
const USER_LIST_FILTER_KEYS_WITH_TELEPHONY = [
  ...USER_LIST_FILTER_KEYS_WITHOUT_TELEPHONY,
  USER_LIST_ORDERING_TELEPHONY_EXTENSION,
];
const USER_LIST_ORDERING_FIELDS_WITHOUT_TELEPHONY = [
  USER_LIST_ORDERING_EMAIL,
  USER_LIST_ORDERING_NAME,
];
const USER_LIST_ORDERING_FIELDS_WITH_TELEPHONY = [
  ...USER_LIST_ORDERING_FIELDS_WITHOUT_TELEPHONY,
  USER_LIST_ORDERING_TELEPHONY_EXTENSION,
];


export default function UsersListTab() {
  const { t, i18n } = useTranslation(['pages', 'common']);
  const { hasPermission } = usePermissions();
  const { isSuperuser, subscriptions, capabilities } = useAuth();
  const [users, setUsers] = useState<UserWithGroups[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [availabilityNowMs, setAvailabilityNowMs] = useState(() => Date.now());
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);

  const flatButtonSx = {
    textTransform: 'none',
    boxShadow: 'none',
  };
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };
  const showTelephonyExtensionColumn = Boolean(capabilities?.hasTelephonyPack);
  const listFilterKeys = useMemo(
    () => (showTelephonyExtensionColumn
      ? USER_LIST_FILTER_KEYS_WITH_TELEPHONY
      : USER_LIST_FILTER_KEYS_WITHOUT_TELEPHONY),
    [showTelephonyExtensionColumn],
  );
  const orderingFields = showTelephonyExtensionColumn
    ? USER_LIST_ORDERING_FIELDS_WITH_TELEPHONY
    : USER_LIST_ORDERING_FIELDS_WITHOUT_TELEPHONY;

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: listFilterKeys,
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: USER_LIST_DEFAULT_ORDERING,
  });

  const statusFilterValue = filters.status ?? '';
  const telephonyAvailabilityFilterValue = filters.telephony_availability ?? '';
  const searchFilterValue = filters.search ?? '';
  const telephonyExtensionFilterValue = showTelephonyExtensionColumn
    ? filters.telephony_extension ?? ''
    : '';
  const requestedOrderingParam = ordering || USER_LIST_DEFAULT_ORDERING;
  const requestedOrderingField = requestedOrderingParam.startsWith('-')
    ? requestedOrderingParam.slice(1)
    : requestedOrderingParam;
  const orderingParam = orderingFields.includes(requestedOrderingField)
    ? requestedOrderingParam
    : USER_LIST_DEFAULT_ORDERING;
  const activeOrderingField = orderingParam.startsWith('-') ? orderingParam.slice(1) : orderingParam;
  const activeOrderingDirection: 'asc' | 'desc' = orderingParam.startsWith('-') ? 'desc' : 'asc';
  const tableColumnCount = showTelephonyExtensionColumn ? 7 : 6;
  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('common:status.active') },
      { value: 'inactive', label: t('common:status.inactive') },
    ],
    [t],
  );
  const telephonyAvailabilityOptions = useMemo(
    () => [
      { value: 'available', label: t('common:operatorAvailability.states.available') },
      { value: 'unavailable', label: t('common:operatorAvailability.states.unavailable') },
      { value: 'logged_out', label: t('common:operatorAvailability.states.loggedOut') },
      { value: 'no_telephony', label: t('common:operatorAvailability.states.noEndpoint') },
    ],
    [t],
  );
  const handleOrderingChange = useCallback(
    (orderingField: string) => {
      const nextDirection =
        activeOrderingField === orderingField && activeOrderingDirection === 'asc' ? 'desc' : 'asc';
      const nextOrdering = nextDirection === 'desc' ? `-${orderingField}` : orderingField;
      setParams({ ordering: nextOrdering }, { resetPage: true });
    },
    [activeOrderingDirection, activeOrderingField, setParams],
  );

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (statusFilterValue === 'active') {
        params.is_active = true;
      } else if (statusFilterValue === 'inactive') {
        params.is_active = false;
      }

      if (telephonyAvailabilityFilterValue) {
        params.telephony_availability = telephonyAvailabilityFilterValue;
      }

      if (showTelephonyExtensionColumn && telephonyExtensionFilterValue) {
        params.telephony_extension = telephonyExtensionFilterValue;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/users/', { params });
      const data = response.data.results ?? response.data;
      setUsers(Array.isArray(data) ? data : []);
      setTotalUsers(response.data.count ?? data.length ?? 0);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [
    orderingParam,
    page,
    rowsPerPage,
    searchFilterValue,
    showTelephonyExtensionColumn,
    statusFilterValue,
    telephonyAvailabilityFilterValue,
    telephonyExtensionFilterValue,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const hasUnavailableUsers = useMemo(
    () => users.some((user) => Boolean(user.telephonyAvailability?.isEligible && !user.telephonyAvailability?.isAvailable)),
    [users],
  );

  useEffect(() => {
    if (!hasUnavailableUsers) {
      return undefined;
    }
    const intervalId = window.setInterval(() => setAvailabilityNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [hasUnavailableUsers]);

  const seatSubscription = subscriptions?.ai?.summary ?? subscriptions?.reverseIvr?.summary ?? null;
  const userSeatStatus = seatSubscription?.userSeats;
  const canCreateBySeat = isSuperuser ? true : (userSeatStatus?.canCreate ?? true);

  const handleDelete = async (id: string) => {
    if (!confirm(t('pages:users.deleteUser.message'))) return;
    try {
      await api.delete(`/api/users/${id}/`);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const filterControls = (
    <>
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:users.filters.searchPlaceholder')}
        value={searchFilterValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setParams({ search: value || undefined }, { resetPage: true });
        }}
        sx={{ ...filterInputSx, minWidth: 240, flex: '1 1 240px' }}
      />
      <FormControl size="small" sx={{ minWidth: 180, ...filterInputSx }}>
        <InputLabel id="users-status-filter-label">
          {t('pages:users.filters.statusLabel')}
        </InputLabel>
        <Select
          labelId="users-status-filter-label"
          value={statusFilterValue}
          label={t('pages:users.filters.statusLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ status: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 200, ...filterInputSx }}>
        <InputLabel id="users-telephony-availability-filter-label">
          {t('pages:users.filters.availabilityLabel')}
        </InputLabel>
        <Select
          labelId="users-telephony-availability-filter-label"
          value={telephonyAvailabilityFilterValue}
          label={t('pages:users.filters.availabilityLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ telephony_availability: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {telephonyAvailabilityOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showTelephonyExtensionColumn && (
        <TextField
          size="small"
          label={t('pages:users.filters.extensionLabel')}
          placeholder={t('pages:users.filters.extensionPlaceholder')}
          value={telephonyExtensionFilterValue}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setParams({ telephony_extension: value || undefined }, { resetPage: true });
          }}
          sx={{ ...filterInputSx, minWidth: 160, flex: '0 1 180px' }}
        />
      )}
      <Box sx={{ ml: 'auto', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <ListActionButton
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchUsers}
        >
          {t('common:actions.refresh')}
        </ListActionButton>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Clear />}
          disabled={
            !searchFilterValue
            && !statusFilterValue
            && !telephonyAvailabilityFilterValue
            && !telephonyExtensionFilterValue
          }
          onClick={() => {
            if (
              !searchFilterValue
              && !statusFilterValue
              && !telephonyAvailabilityFilterValue
              && !telephonyExtensionFilterValue
            ) {
              return;
            }
            setParams(
              { search: null, status: null, telephony_availability: null, telephony_extension: null },
              { resetPage: true },
            );
          }}
          sx={{
            ...flatButtonSx,
            color: theme.palette.primary.main,
            borderColor: alpha(theme.palette.primary.main, 0.6),
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {t('common:actions.clear')}
        </Button>
        <RequirePermission permission="users_create">
          {canCreateBySeat ? (
            <ListActionButton
              variant="contained"
              startIcon={<Add />}
              component={RouterLink}
              to="/users/new"
            >
              {t('pages:users.new')}
            </ListActionButton>
          ) : null}
        </RequirePermission>
      </Box>
    </>
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            },
          }}
        >
          <TableCell sortDirection={activeOrderingField === USER_LIST_ORDERING_EMAIL ? activeOrderingDirection : false}>
            <TableSortLabel
              active={activeOrderingField === USER_LIST_ORDERING_EMAIL}
              direction={activeOrderingField === USER_LIST_ORDERING_EMAIL ? activeOrderingDirection : 'asc'}
              onClick={() => handleOrderingChange(USER_LIST_ORDERING_EMAIL)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {columnTitle(t('pages:users.email'))}
            </TableSortLabel>
          </TableCell>
          <TableCell sortDirection={activeOrderingField === USER_LIST_ORDERING_NAME ? activeOrderingDirection : false}>
            <TableSortLabel
              active={activeOrderingField === USER_LIST_ORDERING_NAME}
              direction={activeOrderingField === USER_LIST_ORDERING_NAME ? activeOrderingDirection : 'asc'}
              onClick={() => handleOrderingChange(USER_LIST_ORDERING_NAME)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {columnTitle(t('common:common.name'))}
            </TableSortLabel>
          </TableCell>
          {showTelephonyExtensionColumn && (
            <TableCell
              sortDirection={
                activeOrderingField === USER_LIST_ORDERING_TELEPHONY_EXTENSION ? activeOrderingDirection : false
              }
            >
              <TableSortLabel
                active={activeOrderingField === USER_LIST_ORDERING_TELEPHONY_EXTENSION}
                direction={
                  activeOrderingField === USER_LIST_ORDERING_TELEPHONY_EXTENSION
                    ? activeOrderingDirection
                    : 'asc'
                }
                onClick={() => handleOrderingChange(USER_LIST_ORDERING_TELEPHONY_EXTENSION)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {columnTitle(t('pages:users.extension.column'))}
              </TableSortLabel>
            </TableCell>
          )}
          <TableCell>{columnTitle(t('pages:users.groups'))}</TableCell>
          <TableCell align="center">
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {columnTitle(t('pages:users.accessProfile'))}
              <Tooltip title={t('pages:users.accessProfileHelp')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {columnTitle(t('pages:users.availability.column'))}
              <Tooltip title={t('pages:users.availability.help')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell align="right">{columnTitle(t('common:common.actions'))}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          [...Array(3)].map((_, index) => (
            <TableRow key={index}>
              {[...Array(tableColumnCount)].map((_, cellIndex) => (
                <TableCell key={cellIndex}><Skeleton /></TableCell>
              ))}
            </TableRow>
          ))
        ) : users.length === 0 ? (
          <TableRow>
            <TableCell colSpan={tableColumnCount} align="center" sx={{ py: 8 }}>
              <Typography color="text.secondary">{t('pages:users.noUsers')}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          users.map((user) => (
            <TableRow
              key={user.id}
              hover
              sx={{
                transition: 'background 0.2s',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <LinkTableCell to={`/users/${user.id}`}>
                <Typography fontWeight={600}>{user.email}</Typography>
              </LinkTableCell>
              <LinkTableCell to={`/users/${user.id}`}>
                {`${user.firstName} ${user.lastName}`.trim() || '-'}
              </LinkTableCell>
              {showTelephonyExtensionColumn && (
                <LinkTableCell to={`/users/${user.id}`}>
                  {user.telephonyEndpoint?.extension?.trim() || '-'}
                </LinkTableCell>
              )}
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {user.groups?.map((group) => (
                    <Chip key={group.id} label={group.name} size="small" variant="outlined" />
                  )) || '-'}
                </Box>
              </TableCell>
              <TableCell align="center">
                {user.isSuperuser ? (
                  <Chip icon={<Shield />} label={t('common:user.superuser')} color="error" size="small" />
                ) : user.isSystemAdmin ? (
                  <Chip icon={<Shield />} label={t('common:user.systemAdmin')} color="warning" size="small" />
                ) : (
                  <Chip label={t('pages:users.roles.standard')} size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell>
                <UserTelephonyAvailabilityStatus
                  availability={user.telephonyAvailability}
                  nowMs={availabilityNowMs}
                  showTimer
                  direction="column"
                />
              </TableCell>
              <TableCell align="right">
                <RequirePermission permission="users_update">
                  <IconButton size="small" component={RouterLink} to={`/users/${user.id}`}>
                    <Edit />
                  </IconButton>
                </RequirePermission>
                <RequirePermission permission="users_delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(user.id)}>
                    <Delete />
                  </IconButton>
                </RequirePermission>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  const tableFooter = totalUsers > 0 ? (
    <LinkPagination
      component="div"
      count={totalUsers}
      page={page}
      onPageChange={(_, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage={t('common:pagination.rowsPerPage')}
      labelDisplayedRows={({ from, to, count }) =>
        count !== -1
          ? `${from}-${to} ${t('common:common.of')} ${count}`
          : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
      }
      getItemAriaLabel={(type) => {
        if (type === 'first') return t('common:pagination.firstPage');
        if (type === 'last') return t('common:pagination.lastPage');
        if (type === 'next') return t('common:pagination.nextPage');
        return t('common:pagination.previousPage');
      }}
      getPageLink={getPageLink}
    />
  ) : undefined;

  if (!hasPermission('users_read')) {
    return <Alert severity="warning">{t('pages:users.accessDenied')}</Alert>;
  }

  return (
    <Box>
      {hasPermission('users_create') && !isSuperuser && !canCreateBySeat && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('pages:users.userSeats.limitReachedList', {
            used: userSeatStatus?.used ?? 0,
            limit: userSeatStatus?.limit ?? 0,
          })}
        </Alert>
      )}
      <FilteredListLayout
        filterControls={filterControls}
        table={table}
        tableFooter={tableFooter}
      />
    </Box>
  );
}
